/**
 * Internationalization Context
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import HttpApi from 'i18next-http-backend';

// Initialize i18next
i18n
  .use(HttpApi)
  .use(initReactI18next)
  .init({
    fallbackLng: 'ja',
    lng: 'ja', // Default language, will be overridden by server settings
    debug: false, // Disable debug after troubleshooting
    interpolation: {
      escapeValue: false
    },
    backend: {
      loadPath: '/locales/{{lng}}/translation.json'
    },
    react: {
      useSuspense: false // Disable suspense to prevent loading issues
    }
  });

interface I18nContextValue {
  t: (key: string, options?: any) => string;
  language: 'ja' | 'en';
  changeLanguage: (lang: 'ja' | 'en') => void;
}

export const I18nContext = createContext<I18nContextValue | undefined>(undefined);

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
};

interface I18nProviderProps {
  children: React.ReactNode;
}


export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const { t, i18n: i18nInstance } = useTranslation();
  const [language, setLanguage] = useState<'ja' | 'en'>(() => {
    // Check URL path first
    const pathname = window.location.pathname;
    if (pathname.startsWith('/en/')) {
      return 'en';
    } else if (pathname.startsWith('/ja/')) {
      return 'ja';
    }
    
    // Check URL parameter
    const params = new URLSearchParams(window.location.search);
    const urlLang = params.get('lang');
    if (urlLang === 'ja' || urlLang === 'en') {
      return urlLang;
    }
    // Get current language from i18n instance
    const defaultLang = (i18nInstance.language as 'ja' | 'en') || 'ja';
    return defaultLang;
  });
  
  // Handle URL path or parameter language on mount (only once)
  useEffect(() => {
    let detectedLang: 'ja' | 'en' | null = null;
    
    // Check URL path first
    const pathname = window.location.pathname;
    if (pathname.startsWith('/en/')) {
      detectedLang = 'en';
    } else if (pathname.startsWith('/ja/')) {
      detectedLang = 'ja';
    } else {
      // Check URL parameter
      const params = new URLSearchParams(window.location.search);
      const urlLang = params.get('lang');
      if (urlLang === 'ja' || urlLang === 'en') {
        detectedLang = urlLang;
      }
    }
    
    if (detectedLang) {
      // Always sync both i18n instance and state
      i18nInstance.changeLanguage(detectedLang);
      setLanguage(detectedLang);
    } else {
      // Sync state with i18n instance if no URL-based language
      const currentLang = (i18nInstance.language as 'ja' | 'en') || 'ja';
      setLanguage(currentLang);
    }
  }, []); // Empty dependency array - only run once on mount

  // Update language state when i18n language changes
  useEffect(() => {
    
    const handleLanguageChanged = (lng: string) => {
      setLanguage(lng as 'ja' | 'en');
    };
    
    i18nInstance.on('languageChanged', handleLanguageChanged);
    
    return () => {
      i18nInstance.off('languageChanged', handleLanguageChanged);
    };
  }, [i18nInstance]);

  const changeLanguage = async (lang: 'ja' | 'en') => {
    try {
      await i18nInstance.changeLanguage(lang);
      setLanguage(lang);
      // Don't use localStorage to prevent conflicts with server settings
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  const wrappedT = (key: string, options?: any): string => {
    return t(key, options) as string;
  };

  const value: I18nContextValue = {
    t: wrappedT,
    language,
    changeLanguage
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};